import {
  createSlice,
  EntityState,
  PayloadAction,
  createEntityAdapter,
} from "@reduxjs/toolkit";
import produce from "immer";
import { RootState } from "app/store";
import {
  Course,
  ProgramSummary,
  ProgramProgress,
  AlgorithmSummary,
  AlgorithmProgress,
  CourseProgramDisplay,
  CourseAlgorithmDisplay,
} from "features/courses/types";
import { getCoursesDisplay, getCourseProgresses } from "features/courses/api";

export const displayAlgorithmAdapter = createEntityAdapter<CourseAlgorithmDisplay>();
export const {
  selectAll: selectAllDisplayAlgorithm,
  selectById: selectDisplayAlgorithmById,
  selectIds: selectDisplayAlgorithmIds,
} = displayAlgorithmAdapter.getSelectors(
  (state: RootState) => state.courses.display.algorithm
);

export const displayProgramAdapter = createEntityAdapter<CourseProgramDisplay>();
export const {
  selectAll: selectAllDisplayProgram,
  selectById: selectDisplayProgramById,
  selectIds: selectDisplayProgramIds,
} = displayProgramAdapter.getSelectors(
  (state: RootState) => state.courses.display.program
);

export const displayNishishinjukuAdapter = createEntityAdapter<CourseAlgorithmDisplay>();
export const {
  selectAll: selectAllDisplayNishishinjuku,
  selectById: selectDisplayNishishinjukuById,
  selectIds: selectDisplayNishishinjukuIds,
} = displayAlgorithmAdapter.getSelectors(
  (state: RootState) => state.courses.display.nishishinjuku
);

export const progressesAlgorithmAdapter = createEntityAdapter<AlgorithmProgress>();
export const {
  selectAll: selectAllProgressesAlgorithm,
  selectById: selectProgresseAlgorithmById,
  selectIds: selectProgresseAlgorithmIds,
} = progressesAlgorithmAdapter.getSelectors(
  (state: RootState) => state.courses.progresses.algorithm.progress
);

export const progressesProgramAdapter = createEntityAdapter<ProgramProgress>();
export const {
  selectAll: selectAllProgressesProgram,
  selectById: selectProgresseProgramById,
  selectIds: selectProgresseProgramIds,
} = progressesProgramAdapter.getSelectors(
  (state: RootState) => state.courses.progresses.program.progress
);

export const progressesNishishinjukuAdapter = createEntityAdapter<AlgorithmProgress>();
export const {
  selectAll: selectAllProgressesNishishinjuku,
  selectById: selectProgresseNishishinjukuById,
  selectIds: selectProgresseNishishinjukuIds,
} = progressesNishishinjukuAdapter.getSelectors(
  (state: RootState) => state.courses.progresses.nishishinjuku.progress
);

interface CourseInitialStateType {
  isCamera: boolean;
  showCourseTop: boolean;
  showCourseList: boolean;
  selectedCourse: Course;
  selectedStage: number;
  display: {
    algorithm: EntityState<CourseAlgorithmDisplay>;
    program: EntityState<CourseProgramDisplay>;
    nishishinjuku: EntityState<CourseAlgorithmDisplay>;
  };
  progresses: {
    algorithm: {
      summary: AlgorithmSummary;
      progress: EntityState<AlgorithmProgress>;
    };
    program: {
      summary: ProgramSummary;
      progress: EntityState<ProgramProgress>;
    };
    nishishinjuku: {
      summary: AlgorithmSummary;
      progress: EntityState<AlgorithmProgress>;
    };
  };
}

const initialState: CourseInitialStateType = {
  isCamera: false,
  showCourseTop: true,
  showCourseList: false,
  selectedCourse: Course.ALGORITHM,
  selectedStage: 1,
  display: {
    algorithm: displayAlgorithmAdapter.getInitialState(),
    program: displayProgramAdapter.getInitialState(),
    nishishinjuku: displayNishishinjukuAdapter.getInitialState(),
  },
  progresses: {
    algorithm: {
      summary: null,
      progress: progressesAlgorithmAdapter.getInitialState(),
    },
    program: {
      summary: null,
      progress: progressesProgramAdapter.getInitialState(),
    },
    nishishinjuku: {
      summary: null,
      progress: progressesNishishinjukuAdapter.getInitialState(),
    },
  },
};

const coursesSlice = createSlice({
  name: "courses",
  initialState,
  reducers: {
    updateShowCourseTop(state, action: PayloadAction<boolean>) {
      state.showCourseTop = action.payload;
    },
    updateShowCourseList(state, action: PayloadAction<boolean>) {
      state.showCourseList = action.payload;
    },
    updateUseCamera(state, action: PayloadAction<boolean>) {
      state.isCamera = action.payload;
    },
    updateSelectedCourse(state, action: PayloadAction<{ course: Course }>) {
      state.selectedStage = 1;
      state.selectedCourse = action.payload.course;
    },
    updateSelectedStage(state, action: PayloadAction<{ stage: number }>) {
      state.selectedStage = action.payload.stage;
    },
    allAlgorithmStageClearAnimation(state) {
      state.progresses.algorithm.summary.all_stage_clear_animation = true;
    },
    allProgramStageClearAnimation(state) {
      state.progresses.program.summary.all_stage_clear_animation = true;
    },
    allNishishinjukuStageClearAnimation(state) {
      state.progresses.nishishinjuku.summary.all_stage_clear_animation = true;
    },
    updateAlgorithmLevelTips(
      state,
      action: PayloadAction<{ stageId: number; stepId: number }>
    ) {
      const { stageId, stepId } = action.payload;
      const progressesAlgorithm = progressesAlgorithmAdapter
        .getSelectors()
        .selectById(state.progresses.algorithm.progress, stageId);
      const newProgressesAlgorithmLevel = produce(
        progressesAlgorithm.level,
        (draft) => {
          draft[stepId - 1].tips = true;
        }
      );
      progressesAlgorithmAdapter.updateOne(
        state.progresses.algorithm.progress,
        {
          id: stageId,
          changes: { level: newProgressesAlgorithmLevel },
        }
      );
    },
    updateAlgorithmLevelProgresses(
      state,
      action: PayloadAction<{
        stageId: number;
        stepId: number;
        totalFinished: number;
        answer: string;
        finishedAt: string;
      }>
    ) {
      const {
        stageId,
        stepId,
        totalFinished,
        answer,
        finishedAt,
      } = action.payload;
      const progressesAlgorithm = progressesAlgorithmAdapter
        .getSelectors()
        .selectById(state.progresses.algorithm.progress, stageId);
      const newProgressesAlgorithm = produce(progressesAlgorithm, (draft) => {
        draft.stage.finished += 1;
        draft.level[stepId - 1].answer = answer;
        draft.level[stepId - 1].is_finished = true;
        draft.level[stepId - 1].finished_at = finishedAt;
      });
      progressesAlgorithmAdapter.updateOne(
        state.progresses.algorithm.progress,
        {
          id: stageId,
          changes: newProgressesAlgorithm,
        }
      );
      state.progresses.algorithm.summary.finished = totalFinished;
    },
    updateAlgorithmLevelAnswer(
      state,
      action: PayloadAction<{
        stageId: number;
        stepId: number;
        answer: string;
      }>
    ) {
      const { stageId, stepId, answer } = action.payload;
      const progressesAlgorithm = progressesAlgorithmAdapter
        .getSelectors()
        .selectById(state.progresses.algorithm.progress, stageId);
      const newProgressesAlgorithm = produce(progressesAlgorithm, (draft) => {
        draft.level[stepId - 1].answer = answer;
      });
      progressesAlgorithmAdapter.updateOne(
        state.progresses.algorithm.progress,
        {
          id: stageId,
          changes: newProgressesAlgorithm,
        }
      );
    },
    updateAlgorithmLevelAnimation(
      state,
      action: PayloadAction<{ stageId: number; stepId: number }>
    ) {
      const { stageId, stepId } = action.payload;
      const progressesAlgorithm = progressesAlgorithmAdapter
        .getSelectors()
        .selectById(state.progresses.algorithm.progress, stageId);
      const newProgressesAlgorithmStage = produce(
        progressesAlgorithm.stage,
        (draft) => {
          draft.animation = stepId;
        }
      );
      progressesAlgorithmAdapter.updateOne(
        state.progresses.algorithm.progress,
        {
          id: stageId,
          changes: { stage: newProgressesAlgorithmStage },
        }
      );
    },
    updateProgramLevelProgresses(
      state,
      action: PayloadAction<{
        stageId: number;
        stepId: number;
        totalFinished: number;
        answer: string;
        finishedAt: string;
      }>
    ) {
      const {
        stageId,
        stepId,
        totalFinished,
        answer,
        finishedAt,
      } = action.payload;
      const progressesProgram = progressesProgramAdapter
        .getSelectors()
        .selectById(state.progresses.program.progress, stageId);
      const newProgressesProgram = produce(progressesProgram, (draft) => {
        draft.stage.finished += 1;
        draft.level[stepId - 1].answer = answer;
        draft.level[stepId - 1].is_finished = true;
        draft.level[stepId - 1].finished_at = finishedAt;
      });
      progressesProgramAdapter.updateOne(state.progresses.program.progress, {
        id: stageId,
        changes: newProgressesProgram,
      });
      state.progresses.program.summary.finished = totalFinished;
    },
    updateProgramLevelAnswer(
      state,
      action: PayloadAction<{
        stageId: number;
        stepId: number;
        answer: string;
      }>
    ) {
      const { stageId, stepId, answer } = action.payload;
      const progressesProgram = progressesProgramAdapter
        .getSelectors()
        .selectById(state.progresses.program.progress, stageId);
      const newProgressesProgram = produce(progressesProgram, (draft) => {
        draft.level[stepId - 1].answer = answer;
      });
      progressesProgramAdapter.updateOne(state.progresses.program.progress, {
        id: stageId,
        changes: newProgressesProgram,
      });
    },
    updateProgramLevelAnimation(
      state,
      action: PayloadAction<{ stageId: number; stepId: number }>
    ) {
      const { stageId, stepId } = action.payload;
      const progressesProgram = progressesProgramAdapter
        .getSelectors()
        .selectById(state.progresses.program.progress, stageId);
      const newProgressesProgramStage = produce(
        progressesProgram.stage,
        (draft) => {
          draft.animation = stepId;
        }
      );
      progressesProgramAdapter.updateOne(state.progresses.program.progress, {
        id: stageId,
        changes: { stage: newProgressesProgramStage },
      });
    },
    updateNishishinjukuLevelTips(
      state,
      action: PayloadAction<{ stageId: number; stepId: number }>
    ) {
      const { stageId, stepId } = action.payload;
      const progressesNishishinjuku = progressesNishishinjukuAdapter
        .getSelectors()
        .selectById(state.progresses.nishishinjuku.progress, stageId);
      const newProgressesNishishinjukuLevel = produce(
        progressesNishishinjuku.level,
        (draft) => {
          draft[stepId - 1].tips = true;
        }
      );
      progressesNishishinjukuAdapter.updateOne(
        state.progresses.nishishinjuku.progress,
        {
          id: stageId,
          changes: { level: newProgressesNishishinjukuLevel },
        }
      );
    },
    updateNishishinjukuLevelProgresses(
      state,
      action: PayloadAction<{
        stageId: number;
        stepId: number;
        totalFinished: number;
        answer: string;
        finishedAt: string;
      }>
    ) {
      const {
        stageId,
        stepId,
        totalFinished,
        answer,
        finishedAt,
      } = action.payload;
      const progressesNishishinjuku = progressesNishishinjukuAdapter
        .getSelectors()
        .selectById(state.progresses.nishishinjuku.progress, stageId);
      const newProgressesNishishinjuku = produce(
        progressesNishishinjuku,
        (draft) => {
          draft.stage.finished += 1;
          draft.level[stepId - 1].answer = answer;
          draft.level[stepId - 1].is_finished = true;
          draft.level[stepId - 1].finished_at = finishedAt;
        }
      );
      progressesNishishinjukuAdapter.updateOne(
        state.progresses.nishishinjuku.progress,
        {
          id: stageId,
          changes: newProgressesNishishinjuku,
        }
      );
      state.progresses.nishishinjuku.summary.finished = totalFinished;
    },
    updateNishishinjukuLevelAnswer(
      state,
      action: PayloadAction<{
        stageId: number;
        stepId: number;
        answer: string;
      }>
    ) {
      const { stageId, stepId, answer } = action.payload;
      const progressesNishishinjuku = progressesNishishinjukuAdapter
        .getSelectors()
        .selectById(state.progresses.nishishinjuku.progress, stageId);
      const newProgressesNishishinjuku = produce(
        progressesNishishinjuku,
        (draft) => {
          draft.level[stepId - 1].answer = answer;
        }
      );
      progressesNishishinjukuAdapter.updateOne(
        state.progresses.nishishinjuku.progress,
        {
          id: stageId,
          changes: newProgressesNishishinjuku,
        }
      );
    },
    updateNishishinjukuLevelAnimation(
      state,
      action: PayloadAction<{ stageId: number; stepId: number }>
    ) {
      const { stageId, stepId } = action.payload;
      const newProgressesNishishinjuku = progressesNishishinjukuAdapter
        .getSelectors()
        .selectById(state.progresses.nishishinjuku.progress, stageId);
      const newProgressesNishishinjukuStage = produce(
        newProgressesNishishinjuku.stage,
        (draft) => {
          draft.animation = stepId;
        }
      );
      progressesNishishinjukuAdapter.updateOne(
        state.progresses.nishishinjuku.progress,
        {
          id: stageId,
          changes: { stage: newProgressesNishishinjukuStage },
        }
      );
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      getCoursesDisplay.fulfilled,
      (state: CourseInitialStateType, action: PayloadAction<any>) => {
        displayAlgorithmAdapter.setAll(
          state.display.algorithm,
          action.payload.algorithm
        );
        displayProgramAdapter.setAll(
          state.display.program,
          action.payload.program
        );
        displayNishishinjukuAdapter.setAll(
          state.display.nishishinjuku,
          action.payload.nishishinjuku
        );
      }
    );
    builder.addCase(
      getCourseProgresses.fulfilled,
      (state: CourseInitialStateType, action: PayloadAction<any>) => {
        progressesAlgorithmAdapter.upsertMany(
          state.progresses.algorithm.progress,
          action.payload.algorithm.progress
        );
        state.progresses.algorithm.summary = action.payload.algorithm.summary;
        progressesProgramAdapter.upsertMany(
          state.progresses.program.progress,
          action.payload.program.progress
        );
        state.progresses.program.summary = action.payload.program.summary;
        progressesNishishinjukuAdapter.upsertMany(
          state.progresses.nishishinjuku.progress,
          action.payload.nishishinjuku.progress
        );
        state.progresses.nishishinjuku.summary =
          action.payload.nishishinjuku.summary;
      }
    );
  },
});
export const actions = { ...coursesSlice.actions };
export default coursesSlice.reducer;
